import { SHARED } from 'app/models/questions/shared';
import { Currency } from 'app/components/FormFields/Currency';
import { CreditScoresRadio } from 'app/components/FormFields/CreditScoresRadio';
import { Name } from 'app/components/FormFields/Name';
import { RadioField } from 'app/components/FormFields/RadioField';
import { Select } from 'app/components/FormFields/Select';
import { Address } from 'app/components/FormFields/Address';
import { ZipCode } from 'app/components/FormFields/ZipCode';
import { State } from 'app/components/FormFields/State';
import { CurrencyAllowZero } from 'app/components/FormFields/CurrencyAllowZero';
import {
  QUANTUM_MARITAL_STATUS_OPTIONS,
  QUANTUM_PROPERTY_TYPE_OPTIONS,
  REVERSE_MORTGAGE_LOAN_PURPOSE,
  REVERSE_PROPERTY_TITLE_HELD,
  YES_OR_NO_OPTIONS,
} from 'app/models/options/options';
import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  hasAddlProperties,
  hasCoBorrower,
  hasMortgageOnProperty,
  hasReverseAgeQuestion,
  hasSelectedNewHome,
  isLessThanTwoYrsAtLivingAddress,
  isMailingDifferent,
} from 'app/models/fields/conditionals';
import { CollectConditionallyRequiredSsn } from 'app/components/FormFields/Ssn/CollectConditionallyRequiredSsn';
import { Integer } from '../../components/FormFields/Integer';
import { AdditionalPropertiesAlternative } from 'app/components/FormFieldArrays/AdditionalPropertiesAlternative';
import { SinglePastDate } from '../../components/FormFields/SinglePastDate';

/**
 * Takes in a field name and returns the corresponding reverse
 * question
 *
 * @param {string} name Field name
 * @returns {Question} Question with matching field name
 */
export const reverseQuestionByName = (name: string): Question => {
  return APPLICATION_REVERSE_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

/**
 * Takes in an array of field names and returns an array of questions
 *
 * @param {string[]} [names=[]] Field names
 * @returns {Question[]} Questions matching field names
 */
export const reverseQuestionsByName = (names: string[] = []): Question[] => {
  const questions: Question[] = [];
  for (const name of names) {
    questions.push(reverseQuestionByName(name));
  }
  return questions;
};

export const APPLICATION_REVERSE_QUESTIONS: Question[] = [
  {
    id: 1001,
    ...SHARED[FieldNames.firstName],
  },
  {
    id: 1002,
    ...SHARED[FieldNames.middleName],
  },
  {
    id: 1003,
    ...SHARED[FieldNames.lastName],
  },
  {
    id: 1004,
    ...SHARED[FieldNames.email],
  },
  {
    id: 1005,
    ...SHARED[FieldNames.phone],
  },
  {
    id: 1006,
    ...SHARED[FieldNames.propertyUsage],
    name: FieldNames.propertyUsage,
    component: Select,
  },
  {
    id        : 1007,
    component : ZipCode,
    label     : 'Zip Code',
    name      : FieldNames.propertyZip,
    showIf    : [hasSelectedNewHome],
  },
  {
    id        : 1008,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.propertyStreet,
    title     : 'Where is the property located?',
    showIf    : [hasSelectedNewHome],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet,
    },
  },
  {
    id        : 1009,
    component : Address,
    label     : 'City',
    name      : FieldNames.propertyCity,
    showIf    : [hasSelectedNewHome],
  },
  {
    id        : 1010,
    component : State,
    label     : 'State',
    name      : FieldNames.propertyState,
    showIf    : [hasSelectedNewHome],
  },
  {
    id       : 1011,
    component: CurrencyAllowZero,
    name     : FieldNames.mortgageBalance,
    showIf   : [hasMortgageOnProperty],
    title    : 'What is the current mortgage balance?',
  },
  {
    id        : 1012,
    component : RadioField,
    name      : FieldNames.maritalStatus,
    options   : QUANTUM_MARITAL_STATUS_OPTIONS,
    title     : 'What is your marital status?',
  },
  {
    id: 1013,
    ...SHARED[FieldNames.dateOfBirth],
    showIf: [(values) => !hasReverseAgeQuestion(values)],
  },
  {
    id: 1014,
    component: RadioField,
    name     : FieldNames.coBorrowerYN,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will you have a co-borrower?',
  },
  {
    id: 1015,
    ...SHARED[FieldNames.firstName],
    name  : FieldNames.coBorrowerFirstName,
    title : 'Tell us about the co-borrower',
    showIf: [hasCoBorrower],
  },
  {
    id: 1016,
    ...SHARED[FieldNames.lastName],
    name  : FieldNames.coBorrowerLastName,
    showIf: [hasCoBorrower],
  },
  {
    id: 1017,
    ...SHARED[FieldNames.dateOfBirth],
    name  : FieldNames.coBorrowerDOB,
    showIf: [(values) => !hasReverseAgeQuestion(values) && hasCoBorrower(values)],
  },
  {
    id       : 1018,
    component: RadioField,
    name     : FieldNames.coBorrowerOnTitle,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Is the Co-Borrower on this title?',
    showIf   : [hasCoBorrower],
  },
  {
    id           : 1019,
    component    : Select,
    label        : 'Property Type',
    name         : FieldNames.propertyType,
    options      : QUANTUM_PROPERTY_TYPE_OPTIONS,
  },
  {
    ...SHARED[FieldNames.propertyZip],
    id: 1020,
    name  : FieldNames.livingPropertyZip,
  },
  {
    id       : 1023,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.livingPropertyStreet,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet,
    },
  },
  {
    id       : 1024,
    component: Address,
    label    : 'City',
    name     : FieldNames.livingPropertyCity,
  },
  {
    id       : 1025,
    component: State,
    label    : 'State',
    name     : FieldNames.livingPropertyState,
  },
  {
    id        : 1026,
    component : Name,
    label     : 'Middle Name',
    name      : FieldNames.coBorrowerMiddleName,
    isOptional: true,
    showIf    : [hasCoBorrower],
  },
  {
    id        : 1027,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.livingPropertyStreet2,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet2,
    },
  },
  {
    ...SHARED[FieldNames.email],
    id     : 1028,
    name   : FieldNames.coBorrowerEmail,
    showIf : [hasCoBorrower],
  },
  {
    ...SHARED[FieldNames.phone],
    id     : 1029,
    name   : FieldNames.coBorrowerPhone,
    showIf : [hasCoBorrower],
  },

  {
    id: 1030,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id: 1031,
    ...SHARED[FieldNames.coBorrowerSuffixName],
  },
  {
    id: 1032,
    ...SHARED[FieldNames.citizenshipType],
    title: "What is your citizenship?",
  },
  {
    id: 1033,
    ...SHARED[FieldNames.coBorrowerCitizenshipType],
  },
  {
    id        : 1034,
    component : RadioField,
    name      : FieldNames.propertyHasMortgage,
    options   : YES_OR_NO_OPTIONS,
    title     : 'Do you have a mortgage on your home?',
  },
  {
    id        : 1035,
    component : RadioField,
    name      : FieldNames.propertyTitleHeld,
    options   : REVERSE_PROPERTY_TITLE_HELD,
    title     : 'How is the property title held?',
  },
  {
    id       : 1041,
    component: AdditionalPropertiesAlternative,
    name     : FieldNames.addlProperties,
    showIf   : [hasAddlProperties],
  },
  {
    id       : 1042,
    component: CurrencyAllowZero,
    label    : 'Total Available Assets',
    name     : FieldNames.assetTotalValue,
  },
  {
    id        : 1043,
    component : Currency,
    name      : FieldNames.homeValue,
    title     : 'not used but is essential for translations',
  },
  {
    id        : 1044,
    component: CurrencyAllowZero,
    label    : 'Co-Borrower Total Available Assets',
    name     : FieldNames.coBorrowerAssetTotalValue,
    showIf: [hasCoBorrower],
  },
  {
    id        : 1045,
    component : RadioField,
    name      : FieldNames.coBorrowerMaritalStatus,
    options   : QUANTUM_MARITAL_STATUS_OPTIONS,
    title     : `Co-borrower's marital status?`,
    showIf    : [hasCoBorrower],
  },
  {
    id       : 1047,
    component: CurrencyAllowZero,
    label    : 'Monthly Income',
    name     : FieldNames.incomeMonthly,
    title    : 'Let\'s collect some information on income and assets',
  },
  {
    id       : 1048,
    component: CurrencyAllowZero,
    title    : 'And for your Co-Borrower',
    label    : 'Co-Borrower Monthly Income',
    name     : FieldNames.coBorrowerIncomeMonthly,
    showIf   : [hasCoBorrower],
  },
  {
    id       : 1049,
    component: CurrencyAllowZero,
    label    : 'Co-Borrower Total Real Estate Assets',
    name     : FieldNames.coBorrowerRealEstateTotalValue,
    showIf   : [hasCoBorrower],
  },
  {
    id         : 1050,
    component  : Currency,
    label      : 'Annual Taxes',
    name       : FieldNames.livingYearTaxes,
  },
  {
    id         : 1051,
    component  : Currency,
    label      : 'Annual Insurance',
    name       : FieldNames.livingYearInsure,
  },
  {
    id         : 1052,
    component  : Currency,
    isOptional : true,
    label      : 'Annual Homeowners Association Fees',
    name       : FieldNames.livingYearAssociate,
  },
  {
    id        : 1053,
    component : RadioField,
    options   : YES_OR_NO_OPTIONS,
    title     : 'Have you paid any of the above property charges late in the last 2 years?',
    name      : FieldNames.hasLatePropertyCharges,
  },
  {
    ...SHARED[FieldNames.hasAddlPropertiesYN],
    id    : 1054,
    title : 'Do you own additional real estate other than this property?',
  },
  {
    id        : 1055,
    component : RadioField,
    title     : 'What is your goal with the Reverse Mortgage?',
    options   : REVERSE_MORTGAGE_LOAN_PURPOSE,
    name      : FieldNames.reverseMortgageLoanPurpose,
  },
  {
    id        : 1056,
    component : Currency,
    title     : 'Tell us about your new home purchase',
    label     : 'Expected Purchase Price',
    name      : FieldNames.purchasePrice,
  },
  {
    id           : 1057,
    component    : Select,
    label        : 'Property Type',
    name         : FieldNames.livingPropertyType,
    options      : QUANTUM_PROPERTY_TYPE_OPTIONS,
  },
  {
    id        : 1058,
    component : RadioField,
    options   : YES_OR_NO_OPTIONS,
    name      : FieldNames.hasSelectedNewHome,
    title     : 'Have you selected a new home?',
  },
  {
    id         : 1059,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.propertyStreet2,
    showIf     : [hasSelectedNewHome],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet2,
    },
  },
  {
    id        : 1060,
    component : CollectConditionallyRequiredSsn,
    name      : FieldNames.ssnQuestionRequirement,
  },
  {
    id: 1061,
    component : Integer,
    name      : FieldNames.age,
    label     : 'Age',
    showIf    : [hasReverseAgeQuestion],
  },
  {
    id: 1062,
    name  : FieldNames.coBorrowerAge,
    component : Integer,
    label     : 'Age',
    showIf: [(values) => hasReverseAgeQuestion(values) && hasCoBorrower(values)],
  },
  {
    id        : 1063,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.livingPrevPropertyStreet,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
    title     : 'Previous Address',
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPrevPropertyStreet,
    },
  },
  {
    id         : 1064,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.livingPrevPropertyStreet2,
    showIf     : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPrevPropertyStreet2,
    },
  },
  {
    id        : 1065,
    component : Address,
    label     : 'City',
    name      : FieldNames.livingPrevPropertyCity,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id        : 1066,
    component : State,
    label     : 'State',
    name      : FieldNames.livingPrevPropertyState,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    ...SHARED[FieldNames.propertyZip],
    id     : 1067,
    name   : FieldNames.livingPrevPropertyZip,
    showIf : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id       : 1068,
    component: SinglePastDate,
    label    : 'Occupancy Start Date',
    name     : FieldNames.livingOccupancyStart,
  },
  {
    id       : 1069,
    component: SinglePastDate,
    label    : 'Occupancy Start Date',
    name     : FieldNames.livingPrevOccupancyStart,
    showIf   : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isBeforeEndDate',
      param: FieldNames.livingOccupancyStart,
    },
  },
  {
    id       : 1070,
    component: RadioField,
    name     : FieldNames.isMailingDifferent,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Is your mailing address different?',
  },
  {
    id        : 1071,
    component : Address,
    isOptional: false,
    label     : 'Street Address',
    name      : FieldNames.mailingStreet,
    showIf: [isMailingDifferent],
  },
  {
    id        : 1072,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.mailingStreet2,
    showIf: [isMailingDifferent],

  },
  {
    id       : 1073,
    component: Address,
    label    : 'City',
    name     : FieldNames.mailingCity,
    showIf   : [isMailingDifferent],
  },
  {
    id       : 1074,
    component: State,
    label    : 'State',
    name     : FieldNames.mailingState,
    showIf   : [isMailingDifferent],
  },
  {
    id       :1075,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.mailingZip,
    showIf   : [isMailingDifferent],
  },
  {
    ...SHARED[FieldNames.propertyUsage],
    id           : 1076,
    component    : Select,
    name         : FieldNames.livingPropertyUsage,
    fieldPopover : true,
  },
  {
    ...SHARED[FieldNames.creditScore],
    id          : 1077,
    component   : CreditScoresRadio,
    title       : 'What is your estimated credit score?',
    fieldPopover: false,
    popover     : null,
  },
];
